// Loading and validating the world registry.
//
// The registry is data (public/art/atlas/bodies.json) and this is the only door
// into it. A missing or mistyped field throws naming the field and the row:
// there is no partial registry, because a body drawn from half a row is a body
// in the wrong place with no way to tell.

import type {
  AlbedoTransferRow,
  Ocean,
  ArtificialFill,
  ArtificialHeight,
  ArtificialHeightClasses,
  Atmosphere,
  AtmosphereField,
  Availability,
  Bake,
  BakeGlobalProduct,
  BakeRegionProvenance,
  BakeRocks,
  BakeStamp,
  BodiesFile,
  BodyClassId,
  BodyRow,
  Coverage,
  CoverageStatus,
  DataFloor,
  DustCurve,
  Ellipsoid,
  KnownPoint,
  MieLobes,
  NamedCrater,
  Orbit,
  OrbitExtraTerms,
  OrbitFit,
  OrbitFitRms,
  OrbitLaplace,
  OrbitLaplaceFit,
  OrbitOsculating,
  OrbitPeriodicTerm,
  OrbitRates,
  OrbitSets,
  OrbitTheory,
  PickBand,
  PoleRates,
  Quadrangle,
  RegionalHeight,
  Corridor,
  CorridorBand,
  RingBrightnessGain,
  RingComponent,
  RingGainDerivation,
  RingGap,
  Rings,
  Rotation,
  Season,
  PlotPalette,
} from './types.ts';
import { GRAVITATIONAL_CONSTANT_M3_KG_S2 } from '../sim/constants.ts';
import { TEST_SWITCHES } from '../urlFlags.ts';

const BODY_CLASSES: readonly string[] = [
  '0_star',
  '1_terrestrial',
  '2_icy_moon',
  '3_asteroid',
  '4_gas_atmosphere',
];

/** The class the cloud-top corridor exists for, and the only one that has one. */
const GAS_ATMOSPHERE_CLASS = '4_gas_atmosphere';

const PICK_BANDS: readonly string[] = ['SYSTEM', 'ORBIT'];

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

function fail(where: string, what: string): never {
  throw new Error(`bodies.json: ${where} ${what}`);
}

function describe(v: unknown): string {
  if (v === null) return 'null';
  if (Array.isArray(v)) return 'an array';
  return typeof v;
}

function object(v: unknown, where: string): Record<string, unknown> {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) {
    fail(where, `is not an object (got ${describe(v)})`);
  }
  return v as Record<string, unknown>;
}

function num(o: Record<string, unknown>, key: string, where: string): number {
  const v = o[key];
  if (v === undefined) fail(`${where}.${key}`, 'is missing');
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    fail(`${where}.${key}`, `is not a finite number (got ${describe(v)})`);
  }
  return v;
}

function numOrNull(o: Record<string, unknown>, key: string, where: string): number | null {
  const v = o[key];
  if (v === undefined) fail(`${where}.${key}`, 'is missing');
  if (v === null) return null;
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    fail(`${where}.${key}`, `is not a finite number or null (got ${describe(v)})`);
  }
  return v;
}

function str(o: Record<string, unknown>, key: string, where: string): string {
  const v = o[key];
  if (v === undefined) fail(`${where}.${key}`, 'is missing');
  if (typeof v !== 'string') fail(`${where}.${key}`, `is not a string (got ${describe(v)})`);
  return v;
}

function nonEmptyStr(o: Record<string, unknown>, key: string, where: string): string {
  const v = str(o, key, where);
  if (v.length === 0) fail(`${where}.${key}`, 'is empty');
  return v;
}

function strOrNull(o: Record<string, unknown>, key: string, where: string): string | null {
  const v = o[key];
  if (v === undefined) fail(`${where}.${key}`, 'is missing');
  if (v === null) return null;
  if (typeof v !== 'string') fail(`${where}.${key}`, `is not a string or null (got ${describe(v)})`);
  return v;
}

function bool(o: Record<string, unknown>, key: string, where: string): boolean {
  const v = o[key];
  if (v === undefined) fail(`${where}.${key}`, 'is missing');
  if (typeof v !== 'boolean') fail(`${where}.${key}`, `is not a boolean (got ${describe(v)})`);
  return v;
}

function oneOf(o: Record<string, unknown>, key: string, where: string, allowed: readonly string[]): string {
  const v = str(o, key, where);
  if (!allowed.includes(v)) {
    fail(`${where}.${key}`, `is not one of ${allowed.join(', ')} (got "${v}")`);
  }
  return v;
}

function numArray(o: Record<string, unknown>, key: string, where: string): readonly number[] {
  const v = o[key];
  if (v === undefined) fail(`${where}.${key}`, 'is missing');
  if (!Array.isArray(v)) fail(`${where}.${key}`, `is not an array (got ${describe(v)})`);
  return v.map((entry, i) => {
    if (typeof entry !== 'number' || !Number.isFinite(entry)) {
      fail(`${where}.${key}[${i}]`, `is not a finite number (got ${describe(entry)})`);
    }
    return entry;
  });
}

function strArrayOrNull(
  o: Record<string, unknown>,
  key: string,
  where: string,
): readonly string[] | null {
  const v = o[key];
  if (v === undefined) fail(`${where}.${key}`, 'is missing');
  if (v === null) return null;
  if (!Array.isArray(v)) fail(`${where}.${key}`, `is not an array or null (got ${describe(v)})`);
  return v.map((entry, i) => {
    if (typeof entry !== 'string') {
      fail(`${where}.${key}[${i}]`, `is not a string (got ${describe(entry)})`);
    }
    return entry;
  });
}

function readEllipsoid(v: unknown, where: string): Ellipsoid {
  const o = object(v, where);
  return { a_m: num(o, 'a_m', where), b_m: num(o, 'b_m', where), c_m: num(o, 'c_m', where) };
}

function readPoleRates(v: unknown, where: string): PoleRates {
  const o = object(v, where);
  return {
    ra_deg_per_cy: num(o, 'ra_deg_per_cy', where),
    dec_deg_per_cy: num(o, 'dec_deg_per_cy', where),
  };
}

function readRotation(v: unknown, where: string): Rotation {
  const o = object(v, where);
  return {
    period_s: num(o, 'period_s', where),
    pole_ra_deg: numOrNull(o, 'pole_ra_deg', where),
    pole_dec_deg: numOrNull(o, 'pole_dec_deg', where),
    pole_rates: readPoleRates(o['pole_rates'], `${where}.pole_rates`),
    prime_meridian_deg: numOrNull(o, 'prime_meridian_deg', where),
    W_rate_deg_day: numOrNull(o, 'W_rate_deg_day', where),
    solar_day_s: numOrNull(o, 'solar_day_s', where),
  };
}

function readSeason(v: unknown, where: string): Season | null {
  if (v === undefined) fail(where, 'is missing');
  if (v === null) return null;
  const o = object(v, where);
  return {
    obliquity_deg: num(o, 'obliquity_deg', where),
    eccentricity: num(o, 'eccentricity', where),
    ls_perihelion_deg: num(o, 'ls_perihelion_deg', where),
    source: nonEmptyStr(o, 'source', where),
  };
}

function readDustCurve(v: unknown, where: string): DustCurve | null {
  if (v === undefined) fail(where, 'is missing');
  if (v === null) return null;
  const o = object(v, where);
  return {
    peak_ls_deg: num(o, 'peak_ls_deg', where),
    width_deg: num(o, 'width_deg', where),
    tau_min: num(o, 'tau_min', where),
    tau_amplitude: num(o, 'tau_amplitude', where),
    source: nonEmptyStr(o, 'source', where),
  };
}

function readOrbitRates(v: unknown, where: string): OrbitRates {
  const o = object(v, where);
  return {
    a_au_cy: num(o, 'a_au_cy', where),
    e_cy: num(o, 'e_cy', where),
    i_deg_cy: num(o, 'i_deg_cy', where),
    L_deg_cy: num(o, 'L_deg_cy', where),
    long_peri_deg_cy: num(o, 'long_peri_deg_cy', where),
    long_node_deg_cy: num(o, 'long_node_deg_cy', where),
  };
}

function readExtraTerms(v: unknown, where: string): OrbitExtraTerms | null {
  if (v === undefined) fail(where, 'is missing');
  if (v === null) return null;
  const o = object(v, where);
  return {
    b: num(o, 'b', where),
    c: num(o, 'c', where),
    s: num(o, 's', where),
    f: num(o, 'f', where),
  };
}

function readOrbitFitRms(v: unknown, where: string): OrbitFitRms {
  const o = object(v, where);
  return {
    a_au: num(o, 'a_au', where),
    e: num(o, 'e', where),
    i_deg: num(o, 'i_deg', where),
    L_deg: num(o, 'L_deg', where),
    long_peri_deg: num(o, 'long_peri_deg', where),
    long_node_deg: num(o, 'long_node_deg', where),
  };
}

function readOrbitFit(v: unknown, where: string): OrbitFit {
  const o = object(v, where);
  const rawNote = o['note'];
  const noteField: { note?: string } = {};
  if (rawNote !== undefined && rawNote !== null) {
    noteField.note = str(o, 'note', where);
  }
  return {
    method: str(o, 'method', where),
    source: str(o, 'source', where),
    center: str(o, 'center', where),
    command: str(o, 'command', where),
    window_start: str(o, 'window_start', where),
    window_stop: str(o, 'window_stop', where),
    sample_step_days: num(o, 'sample_step_days', where),
    sample_count: num(o, 'sample_count', where),
    retrieved: str(o, 'retrieved', where),
    query: str(o, 'query', where),
    rms: readOrbitFitRms(o['rms'], `${where}.rms`),
    ...noteField,
  };
}

function readOrbitOsculating(v: unknown, where: string): OrbitOsculating {
  const o = object(v, where);
  return {
    source: str(o, 'source', where),
    url: str(o, 'url', where),
    retrieved: str(o, 'retrieved', where),
    spacing_days: num(o, 'spacing_days', where),
    epoch_start_jd: num(o, 'epoch_start_jd', where),
    epoch_end_jd: num(o, 'epoch_end_jd', where),
    count: num(o, 'count', where),
  };
}

function readPeriodicTerm(v: unknown, where: string): OrbitPeriodicTerm {
  const o = object(v, where);
  const period = num(o, 'period_days', where);
  if (!(period > 0)) fail(`${where}.period_days`, `is not a period (got ${period})`);
  return { period_days: period, amplitude_deg: num(o, 'amplitude_deg', where), phase_deg: num(o, 'phase_deg', where) };
}

function readOrbitLaplaceFit(v: unknown, where: string): OrbitLaplaceFit {
  const o = object(v, where);
  const rms = object(o['rms'], `${where}.rms`);
  return {
    method: str(o, 'method', where),
    center: str(o, 'center', where),
    command: str(o, 'command', where),
    window_start: str(o, 'window_start', where),
    window_stop: str(o, 'window_stop', where),
    sample_step_days: num(o, 'sample_step_days', where),
    sample_count: num(o, 'sample_count', where),
    retrieved: str(o, 'retrieved', where),
    query: str(o, 'query', where),
    rms: {
      i_deg: num(rms, 'i_deg', `${where}.rms`),
      long_node_deg: num(rms, 'long_node_deg', `${where}.rms`),
      L_deg: num(rms, 'L_deg', `${where}.rms`),
      long_peri_deg: num(rms, 'long_peri_deg', `${where}.rms`),
    },
  };
}

function readOrbitLaplace(v: unknown, where: string): OrbitLaplace {
  const o = object(v, where);
  const i = num(o, 'i_deg', where);
  if (i < 0 || i > 180) fail(`${where}.i_deg`, `is not an inclination (got ${i})`);
  const optional: { L_terms?: readonly OrbitPeriodicTerm[]; fit?: OrbitLaplaceFit } = {};
  const rawTerms = o['L_terms'];
  if (rawTerms !== undefined && rawTerms !== null) {
    if (!Array.isArray(rawTerms)) fail(`${where}.L_terms`, `is not an array (got ${describe(rawTerms)})`);
    optional.L_terms = rawTerms.map((t, k) => readPeriodicTerm(t, `${where}.L_terms[${k}]`));
  }
  if (o['fit'] !== undefined && o['fit'] !== null) optional.fit = readOrbitLaplaceFit(o['fit'], `${where}.fit`);
  return {
    axis_lon_deg: num(o, 'axis_lon_deg', where),
    axis_lat_deg: num(o, 'axis_lat_deg', where),
    i_deg: i,
    long_node_deg: num(o, 'long_node_deg', where),
    long_node_deg_cy: num(o, 'long_node_deg_cy', where),
    L_deg: num(o, 'L_deg', where),
    L_deg_cy: num(o, 'L_deg_cy', where),
    long_peri_deg: num(o, 'long_peri_deg', where),
    long_peri_deg_cy: num(o, 'long_peri_deg_cy', where),
    source: str(o, 'source', where),
    ...optional,
  };
}

const OSCULATING_SET_FIELDS = [
  'a_au',
  'e',
  'i_deg',
  'long_node_deg',
  'long_peri_deg',
  'L_deg',
] as const;

/** Parse and cross-validate one sidecar body's columnar element arrays. */
function readOrbitSets(v: unknown, where: string): OrbitSets {
  const o = object(v, where);
  const epoch_jd = numArray(o, 'epoch_jd', where);
  if (epoch_jd.length === 0) fail(`${where}.epoch_jd`, 'is empty');
  for (let i = 1; i < epoch_jd.length; i++) {
    if (!(epoch_jd[i]! > epoch_jd[i - 1]!)) {
      fail(`${where}.epoch_jd`, `is not strictly ascending at index ${i}`);
    }
  }
  const columns = Object.fromEntries(
    OSCULATING_SET_FIELDS.map((key) => [key, numArray(o, key, where)]),
  ) as Record<(typeof OSCULATING_SET_FIELDS)[number], readonly number[]>;
  for (const key of OSCULATING_SET_FIELDS) {
    const arr = columns[key];
    if (arr.length !== epoch_jd.length) {
      fail(`${where}.${key}`, `has length ${arr.length}, expected ${epoch_jd.length} to match epoch_jd`);
    }
  }
  // Same per-sample bounds readOrbit enforces on the row's own elements - a
  // sidecar sample is still an orbit, at a different instant.
  for (let i = 0; i < epoch_jd.length; i++) {
    const a = columns.a_au[i]!;
    if (!(a > 0)) fail(`${where}.a_au[${i}]`, `is not positive (got ${a})`);
    const e = columns.e[i]!;
    if (!(e >= 0 && e < 1)) fail(`${where}.e[${i}]`, `is not an ellipse eccentricity (got ${e})`);
  }
  const gm = num(o, 'gm_km3_s2', where);
  if (gm <= 0) fail(`${where}.gm_km3_s2`, `is not positive (got ${gm})`);
  const passField: { encounter_jd?: readonly number[] } = {};
  if (o['encounter_jd'] !== undefined) {
    const passes = numArray(o, 'encounter_jd', where);
    let prevInterval = -1;
    for (let i = 0; i < passes.length; i++) {
      const t = passes[i]!;
      let k = 0;
      while (k + 1 < epoch_jd.length && epoch_jd[k + 1]! <= t) k++;
      if (!(t > epoch_jd[k]! && k + 1 < epoch_jd.length && t < epoch_jd[k + 1]!)) {
        fail(`${where}.encounter_jd[${i}]`, `is not strictly between two samples (got ${t})`);
      }
      if (k <= prevInterval) fail(`${where}.encounter_jd[${i}]`, 'is not in a later interval than the one before it');
      // A mark in the sidecar's own final interval leaves its last sample with a
      // pass on one side and the array's own end on the other, not a pass on
      // both - the run-of-one case ephemeris.ts's sampleTangentDeg actually
      // handles is two marks either side, so this one would freeze the last
      // sample's tangent instead of one-siding it.
      if (k === epoch_jd.length - 2) {
        fail(`${where}.encounter_jd[${i}]`, `is in the sidecar's final interval, which leaves its last sample no same-side neighbour to take a tangent from (got ${t})`);
      }
      prevInterval = k;
    }
    passField.encounter_jd = passes;
  }
  return {
    epoch_jd,
    a_au: columns.a_au,
    e: columns.e,
    i_deg: columns.i_deg,
    long_node_deg: columns.long_node_deg,
    long_peri_deg: columns.long_peri_deg,
    L_deg: columns.L_deg,
    ...passField,
    gm_km3_s2: gm,
  };
}

function readOrbit(v: unknown, where: string): Orbit | null {
  if (v === undefined) fail(where, 'is missing');
  if (v === null) return null;
  const o = object(v, where);
  const frame = str(o, 'frame', where);
  if (frame !== 'ecliptic_j2000') {
    fail(`${where}.frame`, `is not ecliptic_j2000 (got "${frame}")`);
  }
  const e = num(o, 'e', where);
  if (e < 0 || e >= 1) fail(`${where}.e`, `is not an ellipse eccentricity (got ${e})`);
  const a = num(o, 'a_au', where);
  if (a <= 0) fail(`${where}.a_au`, `is not positive (got ${a})`);
  const rawFit = o['fit'];
  const fitField: { fit?: OrbitFit } = {};
  if (rawFit !== undefined && rawFit !== null) {
    fitField.fit = readOrbitFit(rawFit, `${where}.fit`);
  }
  const rawOsc = o['osculating'];
  const oscField: { osculating?: OrbitOsculating } = {};
  if (rawOsc !== undefined && rawOsc !== null) {
    oscField.osculating = readOrbitOsculating(rawOsc, `${where}.osculating`);
  }
  const rawLaplace = o['laplace'];
  const laplaceField: { laplace?: OrbitLaplace } = {};
  if (rawLaplace !== undefined && rawLaplace !== null) {
    laplaceField.laplace = readOrbitLaplace(rawLaplace, `${where}.laplace`);
  }
  const rawEnds = o['solution_ends_jd'];
  const endsField: { solution_ends_jd?: number } = {};
  if (rawEnds !== undefined && rawEnds !== null) {
    endsField.solution_ends_jd = num(o, 'solution_ends_jd', where);
  }
  const parentRelative = bool(o, 'parent_relative', where);
  const theoryField: { theory?: OrbitTheory } = {};
  if (o['theory'] !== undefined && o['theory'] !== null) {
    theoryField.theory = oneOf(o, 'theory', where, ORBIT_THEORIES) as OrbitTheory;
    // The one series today is geocentric; the row check below holds its parent to earth.
    if (!parentRelative) fail(`${where}.theory`, 'names a geocentric series on a heliocentric orbit');
  }
  const baryField: { barycentre_with?: string } = {};
  if (o['barycentre_with'] !== undefined && o['barycentre_with'] !== null) {
    baryField.barycentre_with = str(o, 'barycentre_with', where);
  }
  return {
    epoch: str(o, 'epoch', where),
    epoch_jd: num(o, 'epoch_jd', where),
    frame: 'ecliptic_j2000',
    parent_relative: parentRelative,
    a_au: a,
    e,
    i_deg: num(o, 'i_deg', where),
    L_deg: num(o, 'L_deg', where),
    long_peri_deg: num(o, 'long_peri_deg', where),
    long_node_deg: num(o, 'long_node_deg', where),
    rates: readOrbitRates(o['rates'], `${where}.rates`),
    extra_terms: readExtraTerms(o['extra_terms'], `${where}.extra_terms`),
    rates_source: str(o, 'rates_source', where),
    ...fitField,
    ...oscField,
    ...laplaceField,
    ...endsField,
    ...theoryField,
    ...baryField,
  };
}

const ORBIT_THEORIES: readonly string[] = ['meeus_ch47'];

function readDataFloor(v: unknown, where: string): DataFloor | null {
  if (v === undefined) fail(where, 'is missing');
  if (v === null) return null;
  const o = object(v, where);
  const layers = o['regional_height'];
  return {
    height_mpp: numOrNull(o, 'height_mpp', where),
    albedo_mpp: numOrNull(o, 'albedo_mpp', where),
    instrument: strOrNull(o, 'instrument', where),
    rights: strOrNull(o, 'rights', where),
    credit: strOrNull(o, 'credit', where),
    ...(layers === undefined ? {} : { regional_height: readRegionalHeights(layers, `${where}.regional_height`) }),
  };
}

function readRegionalHeights(v: unknown, where: string): readonly RegionalHeight[] {
  if (!Array.isArray(v)) fail(where, `is not an array (got ${describe(v)})`);
  return v.map((raw, i) => {
    const at = `${where}[${i}]`;
    const o = object(raw, at);
    const runs = o['root_runs'];
    if (!Array.isArray(runs) || runs.length === 0) fail(`${at}.root_runs`, 'is not a non-empty array');
    return {
      id: nonEmptyStr(o, 'id', at),
      height_mpp: num(o, 'height_mpp', at),
      product: nonEmptyStr(o, 'product', at),
      level: num(o, 'level', at),
      root_level: num(o, 'root_level', at),
      root_runs: runs.map((r, j) => {
        if (!Array.isArray(r) || r.length !== 4 || r.some((n) => !Number.isInteger(n))) {
          fail(`${at}.root_runs[${j}]`, 'is not [face, x, y0, y1] integers');
        }
        return [r[0], r[1], r[2], r[3]] as const;
      }),
    };
  });
}

function readBakeRegion(v: unknown, where: string): BakeRegionProvenance {
  const o = object(v, where);
  return {
    instrument: nonEmptyStr(o, 'instrument', where),
    rights: nonEmptyStr(o, 'rights', where),
    datum: nonEmptyStr(o, 'datum', where),
    datum_note: nonEmptyStr(o, 'datum_note', where),
  };
}

const BAKE_ROLES: readonly string[] = ['height', 'albedo'];
const BAKE_FORMATS: readonly string[] = ['raster', 'shape_grid', 'shape_mesh'];

function readBakeGlobalProduct(v: unknown, where: string): BakeGlobalProduct {
  const o = object(v, where);
  const gsd = num(o, 'gsd_m', where);
  if (!(gsd > 0)) fail(`${where}.gsd_m`, `is not positive (got ${gsd})`);
  const scaleField: { value_scale?: number } = {};
  if (o['value_scale'] !== undefined && o['value_scale'] !== null) {
    scaleField.value_scale = num(o, 'value_scale', where);
  }
  const offsetField: { value_offset?: number } = {};
  if (o['value_offset'] !== undefined && o['value_offset'] !== null) {
    offsetField.value_offset = num(o, 'value_offset', where);
  }
  const formatField: { format?: 'raster' | 'shape_grid' | 'shape_mesh' } = {};
  if (o['format'] !== undefined && o['format'] !== null) {
    formatField.format = oneOf(o, 'format', where, BAKE_FORMATS) as
      'raster' | 'shape_grid' | 'shape_mesh';
  }
  const downloadField: { download_url?: string } = {};
  if (o['download_url'] !== undefined && o['download_url'] !== null) {
    downloadField.download_url = nonEmptyStr(o, 'download_url', where);
  }
  const westField: { lon_positive_west?: boolean } = {};
  if (o['lon_positive_west'] !== undefined && o['lon_positive_west'] !== null) {
    westField.lon_positive_west = bool(o, 'lon_positive_west', where);
  }
  const creditField: { credit?: string } = {};
  if (o['credit'] !== undefined && o['credit'] !== null) {
    creditField.credit = nonEmptyStr(o, 'credit', where);
  }
  const fillField: { unmeasured_rgb?: string } = {};
  if (o['unmeasured_rgb'] !== undefined && o['unmeasured_rgb'] !== null) {
    const hex = nonEmptyStr(o, 'unmeasured_rgb', where);
    if (!HEX_COLOR.test(hex)) {
      fail(`${where}.unmeasured_rgb`, `is not a #rrggbb colour (got "${hex}")`);
    }
    fillField.unmeasured_rgb = hex;
  }
  const transferField: { albedo_transfer?: AlbedoTransferRow } = {};
  if (o['albedo_transfer'] !== undefined && o['albedo_transfer'] !== null) {
    const tw = `${where}.albedo_transfer`;
    const t = object(o['albedo_transfer'], tw);
    const perDn = num(t, 'reflectance_per_dn', tw);
    if (!(perDn > 0)) fail(`${tw}.reflectance_per_dn`, `is not positive (got ${perDn})`);
    transferField.albedo_transfer = {
      dn_zero: num(t, 'dn_zero', tw), reflectance_per_dn: perDn, source: nonEmptyStr(t, 'source', tw),
    };
  }
  return {
    role: oneOf(o, 'role', where, BAKE_ROLES) as 'height' | 'albedo',
    ...formatField,
    ...downloadField,
    ...westField,
    ...creditField,
    ...fillField,
    ...transferField,
    product: nonEmptyStr(o, 'product', where),
    instrument: nonEmptyStr(o, 'instrument', where),
    rights: nonEmptyStr(o, 'rights', where),
    url: nonEmptyStr(o, 'url', where),
    file: nonEmptyStr(o, 'file', where),
    gsd_m: gsd,
    ...scaleField,
    ...offsetField,
  };
}

function readKnownPoint(v: unknown, where: string): KnownPoint {
  const o = object(v, where);
  const min = num(o, 'min_m', where);
  const max = num(o, 'max_m', where);
  if (!(max > min)) fail(where, `max_m is not greater than min_m (got ${max} <= ${min})`);
  // Optional on the type, present on every registry row: which bake arm reads
  // the band (`bake_pyramid.py --check known` / `--check scale`).
  const raw = o['source'];
  const sourceField: { source?: string } = {};
  if (raw !== undefined && raw !== null) sourceField.source = nonEmptyStr(o, 'source', where);
  return {
    name: nonEmptyStr(o, 'name', where),
    lat_deg: num(o, 'lat_deg', where),
    lon_deg: num(o, 'lon_deg', where),
    min_m: min,
    max_m: max,
    ...sourceField,
  };
}

function readBakeRocks(v: unknown, where: string): BakeRocks {
  const o = object(v, where);
  const kSource = nonEmptyStr(o, 'k_source', where);
  if (kSource === 'none') return { k_source: 'none', a: null, b: null };
  return { k_source: kSource, a: num(o, 'a', where), b: num(o, 'b', where) };
}

function readBakeFloorLevel(o: Record<string, unknown>, key: string, where: string): number {
  const v = num(o, key, where);
  if (!Number.isInteger(v) || v < 0 || v > 15) {
    fail(`${where}.${key}`, `is not an integer level 0 to 15 (got ${v})`);
  }
  return v;
}

/**
 * A floor level that may be declared absent.
 *
 * Either role's floor may be null, and each means the same thing about its own
 * role: no product exists, so a number here would be a promise of data that
 * does not exist. `height_floor_level` is null on an imagery-only body (class
 * (b)) and `albedo_floor_level` on a shape-model body (class (d)), and
 * `readBodyRow` checks each against the row's own
 * `availability.coverage.<role>` so the two cannot disagree.
 */
function readNullableBakeFloorLevel(
  o: Record<string, unknown>, key: string, where: string,
): number | null {
  if (o[key] === null) return null;
  return readBakeFloorLevel(o, key, where);
}

function readBake(v: unknown, where: string): Bake {
  const o = object(v, where);
  const globalRaw = o['global'];
  if (!Array.isArray(globalRaw)) fail(`${where}.global`, `is not an array (got ${describe(globalRaw)})`);
  if (globalRaw.length === 0) fail(`${where}.global`, 'is empty');
  const rawKnown = o['known_points'];
  const knownField: { known_points?: readonly KnownPoint[] } = {};
  if (rawKnown !== undefined && rawKnown !== null) {
    if (!Array.isArray(rawKnown)) {
      fail(`${where}.known_points`, `is not an array (got ${describe(rawKnown)})`);
    }
    knownField.known_points = rawKnown.map(
      (entry, i) => readKnownPoint(entry, `${where}.known_points[${i}]`),
    );
  }
  const rawRocks = o['rocks'];
  const rocksField: { rocks?: BakeRocks } = {};
  if (rawRocks !== undefined && rawRocks !== null) {
    rocksField.rocks = readBakeRocks(rawRocks, `${where}.rocks`);
  }
  const rawRegion = o['region'];
  const regionField: { region?: BakeRegionProvenance } = {};
  if (rawRegion !== undefined && rawRegion !== null) {
    regionField.region = readBakeRegion(rawRegion, `${where}.region`);
  }
  const rawStamp = o['stamp'];
  const stampField: { stamp?: BakeStamp } = {};
  if (rawStamp !== undefined && rawStamp !== null) {
    const s = object(rawStamp, `${where}.stamp`);
    const frac = num(s, 'measured_fraction', `${where}.stamp`);
    if (!(frac >= 0 && frac <= 1)) {
      fail(`${where}.stamp.measured_fraction`, `is not a share of the sphere (got ${frac})`);
    }
    stampField.stamp = { measured_fraction: frac };
  }
  return {
    ...regionField,
    global: globalRaw.map((entry, i) => readBakeGlobalProduct(entry, `${where}.global[${i}]`)),
    height_floor_level: readNullableBakeFloorLevel(o, 'height_floor_level', where),
    albedo_floor_level: readNullableBakeFloorLevel(o, 'albedo_floor_level', where),
    ...knownField,
    ...rocksField,
    ...stampField,
  };
}

function readArtificial(v: unknown, where: string): ArtificialFill {
  const o = object(v, where);
  const seed = num(o, 'seed', where);
  if (!Number.isInteger(seed)) fail(`${where}.seed`, `is not an integer (got ${seed})`);
  const rawSources = o['sources'];
  if (!Array.isArray(rawSources)) {
    fail(`${where}.sources`, `is not an array (got ${describe(rawSources)})`);
  }
  // A fill with no stated basis is a picture presented as knowledge, which is
  // the one thing this block exists to stop.
  if (rawSources.length === 0) fail(`${where}.sources`, 'is empty');
  return {
    method: nonEmptyStr(o, 'method', where),
    seed,
    map: nonEmptyStr(o, 'map', where),
    stamp: nonEmptyStr(o, 'stamp', where),
    basis: nonEmptyStr(o, 'basis', where),
    sources: rawSources.map((s, i) => {
      if (typeof s !== 'string' || s.length === 0) {
        fail(`${where}.sources[${i}]`, `is not a non-empty string (got ${describe(s)})`);
      }
      return s;
    }),
  };
}

function readOcean(v: unknown, where: string): Ocean {
  const o = object(v, where);
  const mask = numArray(o, 'mask_blue_fraction', where);
  if (mask.length !== 2 || !(mask[0]! > 0 && mask[0]! < mask[1]! && mask[1]! < 1)) {
    fail(`${where}.mask_blue_fraction`, `is not two increasing fractions in (0, 1) (got ${describe(mask)})`);
  }
  const body = numArray(o, 'body_reflectance_rgb', where);
  if (body.length !== 3 || body.some((c) => !(c >= 0 && c < 1))) {
    fail(`${where}.body_reflectance_rgb`, `is not three reflectances in [0, 1) (got ${describe(body)})`);
  }
  const n = num(o, 'refractive_index', where);
  if (!(n > 1 && n < 2)) fail(`${where}.refractive_index`, `is not a liquid's index (got ${n})`);
  const slope = num(o, 'slope_variance', where);
  if (!(slope > 0 && slope < 0.5)) fail(`${where}.slope_variance`, `is not a mean-square slope (got ${slope})`);
  const sources = readSourcesArray(o['sources'], `${where}.sources`);
  return {
    mask_blue_fraction: [mask[0]!, mask[1]!],
    body_reflectance_rgb: [body[0]!, body[1]!, body[2]!],
    refractive_index: n,
    slope_variance: slope,
    sources,
  };
}

function readSourcesArray(v: unknown, where: string): readonly string[] {
  if (!Array.isArray(v)) fail(where, `is not an array (got ${describe(v)})`);
  // Relief (or a classification of it) with no stated basis is topography
  // presented as knowledge, which is the one thing this block exists to stop.
  if (v.length === 0) fail(where, 'is empty');
  return v.map((x, i) => {
    if (typeof x !== 'string' || x.length === 0) {
      fail(`${where}[${i}]`, `is not a non-empty string (got ${describe(x)})`);
    }
    return x;
  });
}

function readNamedCrater(v: unknown, where: string): NamedCrater {
  const o = object(v, where);
  return {
    name: nonEmptyStr(o, 'name', where),
    lat_deg: num(o, 'lat_deg', where),
    lon_deg: num(o, 'lon_deg', where),
    diameter_km: num(o, 'diameter_km', where),
    depth_m: num(o, 'depth_m', where),
    source: nonEmptyStr(o, 'source', where),
  };
}

function readArtificialHeightClasses(v: unknown, where: string): ArtificialHeightClasses {
  const o = object(v, where);
  const sharesWhere = `${where}.shares`;
  const sharesRaw = object(o['shares'], sharesWhere);
  const rawCraters = o['craters'];
  if (!Array.isArray(rawCraters)) {
    fail(`${where}.craters`, `is not an array (got ${describe(rawCraters)})`);
  }
  return {
    method: nonEmptyStr(o, 'method', where),
    terrain_map: nonEmptyStr(o, 'terrain_map', where),
    terrain_stamp: nonEmptyStr(o, 'terrain_stamp', where),
    dune_dir_map: nonEmptyStr(o, 'dune_dir_map', where),
    dune_dir_stamp: nonEmptyStr(o, 'dune_dir_stamp', where),
    dark_luma: num(o, 'dark_luma', where),
    dune_lat_deg: num(o, 'dune_lat_deg', where),
    lake_lat_deg: num(o, 'lake_lat_deg', where),
    lake_neighborhood_km: num(o, 'lake_neighborhood_km', where),
    ...(o['lake_abs_luma'] !== undefined && o['lake_abs_luma'] !== null
      ? { lake_abs_luma: num(o, 'lake_abs_luma', where) }
      : {}),
    ...(o['lake_lat_deg_north'] !== undefined && o['lake_lat_deg_north'] !== null
      ? { lake_lat_deg_north: num(o, 'lake_lat_deg_north', where) }
      : {}),
    ...(o['south_lake_cap_deg'] !== undefined && o['south_lake_cap_deg'] !== null
      ? { south_lake_cap_deg: num(o, 'south_lake_cap_deg', where) }
      : {}),
    ...(o['rugged_band_deg'] !== undefined && o['rugged_band_deg'] !== null
      ? { rugged_band_deg: num(o, 'rugged_band_deg', where) }
      : {}),
    shares: {
      plains: num(sharesRaw, 'plains', sharesWhere),
      dunes: num(sharesRaw, 'dunes', sharesWhere),
      rugged: num(sharesRaw, 'rugged', sharesWhere),
      lakes: num(sharesRaw, 'lakes', sharesWhere),
    },
    craters: rawCraters.map((c, i) => readNamedCrater(c, `${where}.craters[${i}]`)),
    basis: nonEmptyStr(o, 'basis', where),
    sources: readSourcesArray(o['sources'], `${where}.sources`),
  };
}

function readArtificialHeight(v: unknown, where: string): ArtificialHeight {
  const o = object(v, where);
  const seed = num(o, 'seed', where);
  if (!Number.isInteger(seed)) fail(`${where}.seed`, `is not an integer (got ${seed})`);
  const sources = readSourcesArray(o['sources'], `${where}.sources`);

  const rawClasses = o['classes'];
  const classesField: { classes?: ArtificialHeightClasses } = {};
  if (rawClasses !== undefined && rawClasses !== null) {
    classesField.classes = readArtificialHeightClasses(rawClasses, `${where}.classes`);
  }

  // The slope-map fields are a group: a row carries all of them (a body whose
  // relief moves the globe's shading) or none (a body carrying `classes`
  // instead, with its metres a runtime generator's job in a later step). A
  // row with neither has nothing this block exists to publish.
  const hasMap = o['map'] !== undefined && o['map'] !== null;
  if (!hasMap && classesField.classes === undefined) {
    fail(where, 'carries neither a slope map nor classes - nothing for this block to publish');
  }
  const sharedField = {
    method: nonEmptyStr(o, 'method', where),
    seed,
    basis: nonEmptyStr(o, 'basis', where),
    sources,
    ...classesField,
  };
  if (!hasMap) return sharedField;

  const full = num(o, 'slope_full_scale', where);
  if (!(full > 0)) fail(`${where}.slope_full_scale`, `is not positive (got ${full})`);
  const relief = num(o, 'relief_m', where);
  const cap = num(o, 'relief_cap_m', where);
  // The row publishes both the field's own amplitude and the cap it was held
  // to, so a row claiming a cap its own field is over is refused here rather
  // than left for a reader to notice.
  if (relief > cap) fail(`${where}.relief_m`, `is ${relief} over a cap of ${cap}`);
  const slope = num(o, 'max_slope', where);
  if (slope > full) fail(`${where}.max_slope`, `is ${slope} over a full scale of ${full}`);
  return {
    ...sharedField,
    map: nonEmptyStr(o, 'map', where),
    stamp: nonEmptyStr(o, 'stamp', where),
    slope_full_scale: full,
    relief_m: relief,
    relief_cap_m: cap,
    max_slope: slope,
  };
}

const COVERAGE_STATUSES: readonly string[] = ['baked', 'pending', 'no_data', 'globe_map'];

/** The statuses that mean "no pyramid for this role", so no floor level. */
const NO_PYRAMID: readonly string[] = ['no_data', 'globe_map'];

function readCoverage(v: unknown, where: string): Coverage {
  const o = object(v, where);
  return {
    height: oneOf(o, 'height', where, COVERAGE_STATUSES) as CoverageStatus,
    albedo: oneOf(o, 'albedo', where, COVERAGE_STATUSES) as CoverageStatus,
  };
}

function readAvailability(v: unknown, where: string): Availability {
  const o = object(v, where);
  const groundField: { ground?: 'smooth' } = {};
  if (o['ground'] !== undefined && o['ground'] !== null) {
    groundField.ground = oneOf(o, 'ground', where, ['smooth']) as 'smooth';
  }
  return {
    descend: bool(o, 'descend', where),
    reason: str(o, 'reason', where),
    coverage: readCoverage(o['coverage'], `${where}.coverage`),
    ...groundField,
  };
}

function readQuadrangles(v: unknown, where: string): readonly Quadrangle[] {
  if (!Array.isArray(v)) fail(where, `is not an array (got ${describe(v)})`);
  return v.map((entry, i) => {
    const o = object(entry, `${where}[${i}]`);
    const at = `${where}[${i}]`;
    return {
      id: str(o, 'id', at),
      name: str(o, 'name', at),
      latMin: num(o, 'latMin', at),
      latMax: num(o, 'latMax', at),
      lonMin: num(o, 'lonMin', at),
      lonMax: num(o, 'lonMax', at),
    };
  });
}

const ATMOSPHERE_GAP_KINDS: readonly string[] = ['no_atmosphere', 'no_parameters'];

function readAtmosphere(v: unknown, where: string): AtmosphereField {
  const o = object(v, where);
  const kind = str(o, 'kind', where);
  if (kind === 'data') {
    const betaR = numArray(o, 'beta_r_per_m', where);
    if (betaR.length !== 3) {
      fail(`${where}.beta_r_per_m`, `has length ${betaR.length}, expected 3 (680/550/440 nm)`);
    }
    const rawOpaque = o['opaque_deck_m'];
    const opaqueField: {
      opaque_deck_m?: number;
      deck_top_m?: number;
      deck_albedo?: readonly [number, number, number];
      deck_floor_flux?: readonly [number, number, number];
    } = {};
    if (rawOpaque !== undefined && rawOpaque !== null) {
      opaqueField.opaque_deck_m = num(o, 'opaque_deck_m', where);
      if (o['deck_top_m'] !== undefined && o['deck_top_m'] !== null) {
        const top = num(o, 'deck_top_m', where);
        if (!(opaqueField.opaque_deck_m > 0)) {
          fail(`${where}.opaque_deck_m`, `holds ${opaqueField.opaque_deck_m}, which is not `
            + `positive, so ${where}.deck_top_m mounts no deck at all`);
        }
        if (!(top > opaqueField.opaque_deck_m)) {
          fail(`${where}.deck_top_m`, `holds ${top}, which is not above the deck base `
            + `(opaque_deck_m ${opaqueField.opaque_deck_m})`);
        }
        opaqueField.deck_top_m = top;
      }
      for (const key of ['deck_albedo', 'deck_floor_flux'] as const) {
        if (o[key] === undefined || o[key] === null) continue;
        if (!(opaqueField.opaque_deck_m > 0)) {
          fail(`${where}.opaque_deck_m`, `holds ${opaqueField.opaque_deck_m}, which is not `
            + `positive, so ${where}.${key} mounts no deck at all`);
        }
        const t = numArray(o, key, where);
        if (t.length !== 3) fail(`${where}.${key}`, `has length ${t.length}, expected 3`);
        for (const v of t) {
          if (!(v >= 0) || v > 1) fail(`${where}.${key}`, `holds ${v}, which is not in [0, 1]`);
        }
        opaqueField[key] = [t[0]!, t[1]!, t[2]!];
      }
    } else {
      for (const key of ['deck_top_m', 'deck_albedo', 'deck_floor_flux'] as const) {
        if (o[key] !== undefined && o[key] !== null) {
          fail(`${where}.${key}`, 'is present on a row that declares no opaque_deck_m');
        }
      }
    }
    // Optional, and absent means a conservative grey scatterer - which is what
    // three of the four rows are, since only Mars's dust has a published
    // visible spectrum to read.
    const ssaField: { aerosol_ssa?: readonly [number, number, number] } = {};
    if (o['aerosol_ssa'] !== undefined && o['aerosol_ssa'] !== null) {
      const ssa = numArray(o, 'aerosol_ssa', where);
      if (ssa.length !== 3) {
        fail(`${where}.aerosol_ssa`, `has length ${ssa.length}, expected 3 (680/550/440 nm)`);
      }
      for (const v of ssa) {
        if (!(v > 0) || v > 1) fail(`${where}.aerosol_ssa`, `holds ${v}, which is not in (0, 1]`);
      }
      ssaField.aerosol_ssa = [ssa[0]!, ssa[1]!, ssa[2]!];
    }
    const extField: { aerosol_ext_rgb?: readonly [number, number, number] } = {};
    if (o['aerosol_ext_rgb'] !== undefined && o['aerosol_ext_rgb'] !== null) {
      const ext = numArray(o, 'aerosol_ext_rgb', where);
      if (ext.length !== 3) {
        fail(`${where}.aerosol_ext_rgb`, `has length ${ext.length}, expected 3 (680/550/440 nm)`);
      }
      for (const v of ext) {
        if (!(v > 0)) fail(`${where}.aerosol_ext_rgb`, `holds ${v}, which is not positive`);
      }
      if (ext[1] !== 1) {
        fail(`${where}.aerosol_ext_rgb`, `holds ${ext[1]} at 550 nm, where beta_m_550_per_m already is the extinction`);
      }
      extField.aerosol_ext_rgb = [ext[0]!, ext[1]!, ext[2]!];
    }
    // Optional, and absent means mie_g's one lobe. Only Mars has a published
    // two-lobe retrieval.
    const mieLobesField: { mie_lobes?: MieLobes } = {};
    if (o['mie_lobes'] !== undefined && o['mie_lobes'] !== null) {
      const at = `${where}.mie_lobes`;
      const lobes = object(o['mie_lobes'], at);
      const triple = (key: string, lo: number, hi: number, closed: boolean): readonly [number, number, number] => {
        const t = numArray(lobes, key, at);
        if (t.length !== 3) fail(`${at}.${key}`, `has length ${t.length}, expected 3 (680/550/440 nm)`);
        for (const v of t) {
          if (closed ? !(v >= lo && v <= hi) : !(v > lo && v < hi)) {
            fail(`${at}.${key}`, `holds ${v}, which is not in ${closed ? '[' : '('}${lo}, ${hi}${closed ? ']' : ')'}`);
          }
        }
        return [t[0]!, t[1]!, t[2]!];
      };
      const g2 = num(lobes, 'g2', at);
      if (!(g2 > -1) || !(g2 < 1)) fail(`${at}.g2`, `holds ${g2}, which is not in (-1, 1)`);
      mieLobesField.mie_lobes = {
        g1_rgb: triple('g1_rgb', -1, 1, false),
        g2,
        alpha_rgb: triple('alpha_rgb', 0, 1, true),
      };
    }
    // Optional, and absent means DtmPlot's own neutral grey pair - only Mars
    // has a published dust colour to read.
    const rampField: {
      albedo_ramp_low?: readonly [number, number, number];
      albedo_ramp_high?: readonly [number, number, number];
    } = {};
    if (o['albedo_ramp_low'] !== undefined && o['albedo_ramp_low'] !== null) {
      const low = numArray(o, 'albedo_ramp_low', where);
      const high = numArray(o, 'albedo_ramp_high', where);
      if (low.length !== 3) fail(`${where}.albedo_ramp_low`, `has length ${low.length}, expected 3`);
      if (high.length !== 3) fail(`${where}.albedo_ramp_high`, `has length ${high.length}, expected 3`);
      rampField.albedo_ramp_low = [low[0]!, low[1]!, low[2]!];
      rampField.albedo_ramp_high = [high[0]!, high[1]!, high[2]!];
    }
    const atmosphere: Atmosphere = {
      kind: 'data',
      gas: nonEmptyStr(o, 'gas', where),
      p_surface_pa: num(o, 'p_surface_pa', where),
      t_k: num(o, 't_k', where),
      h_rayleigh_m: num(o, 'h_rayleigh_m', where),
      beta_r_per_m: [betaR[0]!, betaR[1]!, betaR[2]!],
      aerosol: nonEmptyStr(o, 'aerosol', where),
      h_aerosol_m: num(o, 'h_aerosol_m', where),
      beta_m_550_per_m: num(o, 'beta_m_550_per_m', where),
      ...ssaField,
      ...extField,
      mie_g: num(o, 'mie_g', where),
      ...mieLobesField,
      ground_albedo: num(o, 'ground_albedo', where),
      ...opaqueField,
      ...rampField,
      dust_curve: readDustCurve(o['dust_curve'], `${where}.dust_curve`),
      source: nonEmptyStr(o, 'source', where),
    };
    return atmosphere;
  }
  if (!ATMOSPHERE_GAP_KINDS.includes(kind)) {
    fail(`${where}.kind`, `is not "data", "no_atmosphere" or "no_parameters" (got "${kind}")`);
  }
  return {
    kind: kind as 'no_atmosphere' | 'no_parameters',
    source: nonEmptyStr(o, 'source', where),
  };
}

/**
 * The step a brightness factor was derived from.
 *
 * Every field is `tools/atlas/bake_rings.py --gain` reading the baked profile
 * and this row at the declared ORBIT pose, so a row that carries a factor with
 * no derivation behind it is a row nobody derived; `bake_rings.py --check`
 * recomputes all of it and refuses a row that has drifted from it.
 */
function readRingGainDerivation(v: unknown, where: string): RingGainDerivation {
  const o = object(v, where);
  const pos = (key: string): number => {
    const x = num(o, key, where);
    if (!(x > 0)) fail(`${where}.${key}`, `is not positive (got ${x})`);
    return x;
  };
  const unit = (key: string): number => {
    const x = num(o, key, where);
    if (!(x >= 0 && x <= 1)) fail(`${where}.${key}`, `is not inside [0, 1] (got ${x})`);
    return x;
  };
  const azimuths = num(o, 'azimuths_drawn', where);
  if (!Number.isInteger(azimuths) || azimuths < 1) {
    fail(`${where}.azimuths_drawn`, `is not a positive whole count (got ${azimuths})`);
  }
  return {
    radius_km: pos('radius_km'),
    footprint_km: pos('footprint_km'),
    azimuths_drawn: azimuths,
    mu_sun: pos('mu_sun'),
    mu_view: pos('mu_view'),
    term: unit('term'),
    alpha: unit('alpha'),
    worst_sun_azimuth_deg: num(o, 'worst_sun_azimuth_deg', where),
    step_grey_as_measured: num(o, 'step_grey_as_measured', where),
    step_grey_at_factor: num(o, 'step_grey_at_factor', where),
    needed_factor: pos('needed_factor'),
  };
}

/**
 * A ring's declared brightness stretch.
 *
 * Two rules the runtime enforces here rather than trusting: the factor may not
 * lift the particles past a perfectly white ring of the same tau
 * (`factor * particle_albedo <= albedo_cap`), and a row that claims a stretch
 * and a row that says it is too faint to draw may not be the same row.
 */
function readRingBrightnessGain(
  v: unknown, where: string, particleAlbedo: number,
): RingBrightnessGain {
  const o = object(v, where);
  const factor = num(o, 'factor', where);
  if (!(factor >= 1)) fail(`${where}.factor`, `is not at least 1 (got ${factor})`);
  const target = num(o, 'target_grey', where);
  if (!(target > 0)) fail(`${where}.target_grey`, `is not positive (got ${target})`);
  const cap = num(o, 'albedo_cap', where);
  if (!(cap > 0 && cap <= 1)) fail(`${where}.albedo_cap`, `is not inside (0, 1] (got ${cap})`);
  if (factor * particleAlbedo > cap + 1e-9) {
    fail(`${where}.factor`, `lifts the particle albedo to ${(factor * particleAlbedo).toFixed(4)}, `
      + `past the ${cap} a perfectly white ring of the same optical depth would reflect`);
  }
  const tooFaint = bool(o, 'too_faint', where);
  if (tooFaint && factor !== 1) {
    fail(`${where}.factor`, `is ${factor} on a row that says its rings are too faint to draw`);
  }
  return {
    factor,
    target_grey: target,
    albedo_cap: cap,
    too_faint: tooFaint,
    derivation: readRingGainDerivation(o['derivation'], `${where}.derivation`),
    source: nonEmptyStr(o, 'source', where),
  };
}

/**
 * One published stretch of a ring system.
 *
 * The albedo rule is here rather than in the shader: the annulus is drawn with
 * one single-scattering albedo per body, so a component that publishes its own
 * is refused until the generated source carries a tau-weighted mixture term.
 * Nothing publishes one today, and drawing a component at an albedo nothing
 * carries is exactly the fault `rings.particle_albedo` was made registry data
 * for.
 */
function readRingComponent(
  v: unknown, where: string, bodyAlbedo: number,
): RingComponent {
  const o = object(v, where);
  const inner = num(o, 'inner_m', where);
  if (!(inner > 0)) fail(`${where}.inner_m`, `is not positive (got ${inner})`);
  const outer = num(o, 'outer_m', where);
  if (!(outer > inner)) {
    fail(`${where}.outer_m`, `is not greater than inner_m (got ${outer} <= ${inner})`);
  }
  const rawProfile = o['profile'];
  const rawTau = o['tau_constant'];
  const hasProfile = rawProfile !== undefined && rawProfile !== null;
  const hasTau = rawTau !== undefined && rawTau !== null;
  if (hasProfile === hasTau) {
    fail(where, `must carry exactly one of profile or tau_constant (got profile=${hasProfile}, tau_constant=${hasTau})`);
  }
  const profileField: { profile?: string } = {};
  if (hasProfile) profileField.profile = nonEmptyStr(o, 'profile', where);
  const tauField: { tau_constant?: number } = {};
  if (hasTau) {
    const tau = num(o, 'tau_constant', where);
    if (!(tau >= 0)) fail(`${where}.tau_constant`, `is not >= 0 (got ${tau})`);
    tauField.tau_constant = tau;
  }
  const albedoField: { particle_albedo?: number } = {};
  const rawAlbedo = o['particle_albedo'];
  if (rawAlbedo !== undefined && rawAlbedo !== null) {
    const a = num(o, 'particle_albedo', where);
    if (!(a > 0 && a <= 1)) {
      fail(`${where}.particle_albedo`, `is not inside (0, 1] (got ${a})`);
    }
    if (Math.abs(a - bodyAlbedo) > 1e-12) {
      fail(`${where}.particle_albedo`, `is ${a} where the block declares ${bodyAlbedo}; the `
        + 'annulus is drawn with one single-scattering albedo per body, so a per-component one '
        + 'needs the tau-weighted mixture term src/atlas/rings.ts does not generate');
    }
    albedoField.particle_albedo = a;
  }
  const rawCite = o['cite'];
  const citeField: { cite?: string } = {};
  if (rawCite !== undefined && rawCite !== null) citeField.cite = nonEmptyStr(o, 'cite', where);
  return {
    name: nonEmptyStr(o, 'name', where),
    inner_m: inner,
    outer_m: outer,
    ...profileField,
    ...tauField,
    ...albedoField,
    source: nonEmptyStr(o, 'source', where),
    ...citeField,
  };
}

/** A named stretch this system has and does not draw. */
function readRingGap(v: unknown, where: string): RingGap {
  const o = object(v, where);
  const inner = num(o, 'inner_m', where);
  if (!(inner > 0)) fail(`${where}.inner_m`, `is not positive (got ${inner})`);
  const outer = num(o, 'outer_m', where);
  if (!(outer > inner)) {
    fail(`${where}.outer_m`, `is not greater than inner_m (got ${outer} <= ${inner})`);
  }
  const published = strArrayOrNull(o, 'published', where);
  if (!published || published.length < 1) {
    fail(`${where}.published`, 'carries no published statement, so nothing says what is known '
      + 'about the part of this system that is not drawn');
  }
  for (const [i, s] of published.entries()) {
    if (!s.trim()) fail(`${where}.published[${i}]`, 'is empty');
  }
  return {
    name: nonEmptyStr(o, 'name', where),
    inner_m: inner,
    outer_m: outer,
    reason: nonEmptyStr(o, 'reason', where),
    published,
  };
}

/**
 * A body's whole ring system, as a list of components over one annulus.
 *
 * `inner_m` and `outer_m` are DERIVED as the union rather than declared, so a
 * component added outside the annulus widens the mesh and the ring shadow's
 * own span with it instead of being silently clipped by a bound nobody moved.
 */
function readRings(v: unknown, where: string): Rings {
  const o = object(v, where);
  const albedo = num(o, 'particle_albedo', where);
  if (!(albedo > 0 && albedo <= 1)) {
    fail(`${where}.particle_albedo`, `is not inside (0, 1] (got ${albedo})`);
  }
  const raw = o['components'];
  if (!Array.isArray(raw) || raw.length === 0) {
    fail(`${where}.components`, `is not a non-empty array (got ${describe(raw)})`);
  }
  const components = (raw as readonly unknown[]).map(
    (c, i) => readRingComponent(c, `${where}.components[${i}]`, albedo));
  const seen = new Set<string>();
  for (const c of components) {
    if (seen.has(c.name)) fail(`${where}.components`, `names "${c.name}" twice`);
    seen.add(c.name);
  }
  const inner = Math.min(...components.map((c) => c.inner_m));
  const outer = Math.max(...components.map((c) => c.outer_m));
  const rawGaps = o['gaps'];
  const gaps = rawGaps === undefined || rawGaps === null
    ? []
    : (Array.isArray(rawGaps)
      ? (rawGaps as readonly unknown[]).map((g, i) => readRingGap(g, `${where}.gaps[${i}]`))
      : fail(`${where}.gaps`, `is not an array (got ${describe(rawGaps)})`));
  return {
    components,
    inner_m: inner,
    outer_m: outer,
    particle_albedo: albedo,
    brightness_gain: readRingBrightnessGain(
      o['brightness_gain'], `${where}.brightness_gain`, albedo),
    gaps,
    source: nonEmptyStr(o, 'source', where),
  };
}

/**
 * A class (c) body's cloud-top corridor.
 *
 * Everything the descent reads off it is checked here: three bands, contiguous,
 * deck outward, the lowest starting AT the deck. Nothing downstream re-derives
 * the ordering, so a row that got it wrong would clamp the camera to whichever
 * band happened to be first in the array.
 */
function readCorridor(v: unknown, where: string): Corridor {
  const o = object(v, where);
  const h = num(o, 'scale_height_m', where);
  if (!(h > 0)) fail(`${where}.scale_height_m`, `is not positive (got ${h})`);
  const deck = num(o, 'deck_m', where);
  if (!(deck >= 0)) fail(`${where}.deck_m`, `is not >= 0 (got ${deck})`);
  const raw = o['bands'];
  if (!Array.isArray(raw)) fail(`${where}.bands`, `is not an array (got ${describe(raw)})`);
  if (raw.length !== 3) {
    fail(`${where}.bands`, `has ${raw.length} entries and canon 5.4 specifies three`);
  }
  const bands: CorridorBand[] = raw.map((entry, i) => {
    const b = object(entry, `${where}.bands[${i}]`);
    const at = `${where}.bands[${i}]`;
    const bottom = num(b, 'bottom_m', at);
    const top = num(b, 'top_m', at);
    if (!(top > bottom)) fail(`${at}.top_m`, `is not above bottom_m (got ${top} <= ${bottom})`);
    const he3 = numOrNull(b, 'he3_mole_fraction', at);
    if (he3 !== null && !(he3 > 0)) {
      fail(`${at}.he3_mole_fraction`, `is not positive or null (got ${he3})`);
    }
    return {
      id: nonEmptyStr(b, 'id', at),
      name: nonEmptyStr(b, 'name', at),
      bottom_m: bottom,
      top_m: top,
      he3_mole_fraction: he3,
    };
  });
  if (bands[0]!.bottom_m !== 0) {
    fail(`${where}.bands[0].bottom_m`,
      `is ${bands[0]!.bottom_m} and the lowest band has to start at the deck (0)`);
  }
  for (let i = 1; i < bands.length; i++) {
    if (bands[i]!.bottom_m !== bands[i - 1]!.top_m) {
      fail(`${where}.bands[${i}].bottom_m`,
        `is ${bands[i]!.bottom_m} and the band below it tops out at ${bands[i - 1]!.top_m}: `
        + 'the corridor has a gap or an overlap in it');
    }
  }
  const perijove = numOrNull(o, 'scoop_perijove_m', where);
  if (perijove !== null && !(perijove > 0)) {
    fail(`${where}.scoop_perijove_m`, `is not positive or null (got ${perijove})`);
  }
  return {
    scale_height_m: h,
    deck_m: deck,
    bands,
    scoop_perijove_m: perijove,
    source: nonEmptyStr(o, 'source', where),
  };
}

function readBody(v: unknown, index: number): BodyRow {
  const o = object(v, `bodies[${index}]`);
  const id = str(o, 'id', `bodies[${index}]`);
  const where = `body "${id}"`;
  const color = str(o, 'albedo_color', where);
  const palette = readPlotPalette(o['plot_palette'], `${where}.plot_palette`);
  const paletteField = palette ? { plot_palette: palette } : {};
  if (!HEX_COLOR.test(color)) {
    fail(`${where}.albedo_color`, `is not a #rrggbb colour (got "${color}")`);
  }
  const radius = num(o, 'radius_m', where);
  if (radius <= 0) fail(`${where}.radius_m`, `is not positive (got ${radius})`);
  const h3 = o['default_h3_resolution'];
  const h3Field: { default_h3_resolution?: number } = {};
  if (h3 !== undefined) {
    const res = num(o, 'default_h3_resolution', where);
    if (!Number.isInteger(res) || res < 0 || res > 15) {
      fail(`${where}.default_h3_resolution`, `is not an H3 resolution 0 to 15 (got ${res})`);
    }
    h3Field.default_h3_resolution = res;
  }
  const rawBake = o['bake'];
  const bakeField: { bake?: Bake } = {};
  if (rawBake !== undefined && rawBake !== null) {
    bakeField.bake = readBake(rawBake, `${where}.bake`);
  }
  const rawArtificial = o['artificial'];
  const artificialField: { artificial?: ArtificialFill } = {};
  if (rawArtificial !== undefined && rawArtificial !== null) {
    artificialField.artificial = readArtificial(rawArtificial, `${where}.artificial`);
  }
  const rawOcean = o['ocean'];
  const oceanField: { ocean?: Ocean } = {};
  if (rawOcean !== undefined && rawOcean !== null) oceanField.ocean = readOcean(rawOcean, `${where}.ocean`);
  const rawRelief = o['artificial_height'];
  const reliefField: { artificial_height?: ArtificialHeight } = {};
  if (rawRelief !== undefined && rawRelief !== null) {
    reliefField.artificial_height = readArtificialHeight(rawRelief, `${where}.artificial_height`);
  }
  const availability = readAvailability(o['availability'], `${where}.availability`);
  // The two statements about elevation cannot disagree: a null height floor is
  // "no DTM exists" and so is a `no_data` height coverage, and a row that said
  // one without the other would let the descent look for a pyramid the ladder
  // says nobody has published.
  const floor = bakeField.bake?.height_floor_level;
  if (floor === null && availability.coverage.height !== 'no_data') {
    fail(`${where}.availability.coverage.height`,
      `is ${availability.coverage.height} but bake.height_floor_level is null`);
  }
  if (floor !== null && floor !== undefined && availability.coverage.height === 'no_data') {
    fail(`${where}.bake.height_floor_level`,
      `is ${floor} but availability.coverage.height says no_data`);
  }
  // Imagery says the same thing with one more way to have none: `globe_map`
  // is measured imagery cut into the 2:1 globe map and no further, which is
  // Sol's, and it carries no floor level for the same reason `no_data` does.
  const albedoFloor = bakeField.bake?.albedo_floor_level;
  if (albedoFloor === null && !NO_PYRAMID.includes(availability.coverage.albedo)) {
    fail(`${where}.availability.coverage.albedo`,
      `is ${availability.coverage.albedo} but bake.albedo_floor_level is null`);
  }
  if (albedoFloor !== null && albedoFloor !== undefined
      && NO_PYRAMID.includes(availability.coverage.albedo)) {
    fail(`${where}.bake.albedo_floor_level`,
      `is ${albedoFloor} but availability.coverage.albedo says `
      + `${availability.coverage.albedo}`);
  }
  const rawRings = o['rings'];
  const ringsField: { rings?: Rings } = {};
  if (rawRings !== undefined && rawRings !== null) {
    ringsField.rings = readRings(rawRings, `${where}.rings`);
  }
  const corridor = o['corridor'] === undefined || o['corridor'] === null
    ? null
    : readCorridor(o['corridor'], `${where}.corridor`);
  // The class and the corridor are one statement, both ways: a gas atmosphere
  // is the class the corridor view exists for, and a corridor on a body with
  // ground would put a floor above a surface the pyramid can reach.
  const bodyClass = oneOf(o, 'class', where, BODY_CLASSES) as BodyClassId;
  if (bodyClass === GAS_ATMOSPHERE_CLASS && corridor === null) {
    fail(`${where}.corridor`, `is missing on a ${GAS_ATMOSPHERE_CLASS} row`);
  }
  if (corridor !== null && bodyClass !== GAS_ATMOSPHERE_CLASS) {
    fail(`${where}.corridor`, `is on a ${bodyClass} row and only ${GAS_ATMOSPHERE_CLASS} has one`);
  }
  if (corridor !== null && availability.descend) {
    fail(`${where}.availability.descend`,
      'is true on a row with a cloud-top corridor, which has no surface to descend to');
  }
  // Smooth ground stands in for a height pyramid nobody published, over imagery
  // that was: with heights it would hide them, and with no imagery or no
  // descent there is nothing to stand on.
  if (availability.ground === 'smooth') {
    if (availability.coverage.height !== 'no_data') {
      fail(`${where}.availability.ground`,
        `is smooth but coverage.height is ${availability.coverage.height}, not no_data`);
    }
    if (availability.coverage.albedo !== 'baked') {
      fail(`${where}.availability.ground`,
        `is smooth but coverage.albedo is ${availability.coverage.albedo}, not baked`);
    }
    if (!availability.descend) {
      fail(`${where}.availability.ground`, 'is smooth on a row whose descend is false');
    }
  }
  // A row's `classes` hand the runtime generator ground to displace; that is
  // only safe where there is no baked height underneath for it to overwrite.
  if (reliefField.artificial_height?.classes !== undefined
      && availability.coverage.height !== 'no_data') {
    fail(`${where}.artificial_height.classes`,
      `is present but coverage.height is ${availability.coverage.height}, not no_data`);
  }
  const rawQuads = o['quadrangles'];
  const quadranglesField: { quadrangles?: readonly Quadrangle[] } = {};
  if (rawQuads !== undefined && rawQuads !== null) {
    quadranglesField.quadrangles = readQuadrangles(rawQuads, `${where}.quadrangles`);
  }
  const orbit = readOrbit(o['orbit'], `${where}.orbit`);
  const parent = strOrNull(o, 'parent', where);
  if (orbit?.theory !== undefined && parent !== 'earth') {
    fail(`${where}.orbit.theory`, `is a geocentric series, and this row orbits "${parent}"`);
  }
  return {
    id,
    display_name: str(o, 'display_name', where),
    class: bodyClass,
    parent,
    radius_m: radius,
    datum: strOrNull(o, 'datum', where),
    ellipsoid: readEllipsoid(o['ellipsoid'], `${where}.ellipsoid`),
    mass_kg: num(o, 'mass_kg', where),
    surface_gravity_m_s2: num(o, 'surface_gravity_m_s2', where),
    rotation: readRotation(o['rotation'], `${where}.rotation`),
    orbit,
    season: readSeason(o['season'], `${where}.season`),
    solar_flux_mean_W_m2: numOrNull(o, 'solar_flux_mean_W_m2', where),
    ...h3Field,
    albedo_color: color,
    ...paletteField,
    texture: strOrNull(o, 'texture', where),
    pick_band: oneOf(o, 'pick_band', where, PICK_BANDS) as PickBand,
    data_floor: readDataFloor(o['data_floor'], `${where}.data_floor`),
    ...bakeField,
    ...artificialField,
    ...reliefField,
    atmosphere: readAtmosphere(o['atmosphere'], `${where}.atmosphere`),
    ...oceanField,
    ...ringsField,
    corridor,
    availability,
    ...quadranglesField,
    sovereign_pattern: strOrNull(o, 'sovereign_pattern', where),
    canonical_houses: strArrayOrNull(o, 'canonical_houses', where),
    legacy_nft_chain: strOrNull(o, 'legacy_nft_chain', where),
    source: str(o, 'source', where),
  };
}

/** The walkaround's own colours for one body. Absent means the neutral form. */
function readPlotPalette(v: unknown, where: string): PlotPalette | undefined {
  if (v === undefined || v === null) return undefined;
  const o = object(v, where);
  const triple = (key: string): readonly [number, number, number] => {
    const a = numArray(o, key, where);
    if (a.length !== 3) fail(`${where}.${key}`, `has length ${a.length}, expected 3`);
    for (const c of a) {
      if (!(c >= 0) || c > 4) fail(`${where}.${key}`, `holds ${c}, which is not a linear rgb channel in [0, 4]`);
    }
    return [a[0]!, a[1]!, a[2]!];
  };
  return {
    sun_rgb: triple('sun_rgb'),
    sky_rgb: triple('sky_rgb'),
    bounce_rgb: triple('bounce_rgb'),
    wall_fill_rgb: triple('wall_fill_rgb'),
    wall_edge_rgb: triple('wall_edge_rgb'),
    wall_low_rgb: triple('wall_low_rgb'),
    wall_high_rgb: triple('wall_high_rgb'),
    plain_low_rgb: triple('plain_low_rgb'),
    plain_high_rgb: triple('plain_high_rgb'),
    source: nonEmptyStr(o, 'source', where),
  };
}

/**
 * Validate a parsed bodies.json. Pure: throws on the first bad field, naming
 * the field and the row it is in, and otherwise returns the rows.
 */
export function validateBodies(json: unknown): readonly BodyRow[] {
  const top = object(json, 'file');
  num(top, 'version', 'file');
  str(top, 'generated', 'file');
  const raw = top['bodies'];
  if (raw === undefined) fail('file.bodies', 'is missing');
  if (!Array.isArray(raw)) fail('file.bodies', `is not an array (got ${describe(raw)})`);
  if (raw.length === 0) fail('file.bodies', 'is empty');
  const rows = raw.map((row, i) => readBody(row, i));

  const seen = new Set<string>();
  for (const row of rows) {
    if (seen.has(row.id)) fail(`body "${row.id}"`, 'has a duplicate id');
    seen.add(row.id);
  }
  for (const row of rows) {
    if (row.parent !== null && !seen.has(row.parent)) {
      fail(`body "${row.id}".parent`, `names "${row.parent}", which is not in the registry`);
    }
    if (row.orbit !== null && row.orbit.parent_relative && row.parent === null) {
      fail(`body "${row.id}".orbit.parent_relative`, 'is true but parent is null');
    }
    const partnerId = row.orbit?.barycentre_with;
    if (partnerId !== undefined) {
      if (row.orbit?.osculating !== undefined) {
        fail(`body "${row.id}".orbit.barycentre_with`, `is declared beside orbit.osculating; the sidecar would be fitted to "${row.id}"'s own centre and the barycentre step would be added on top`);
      }
      const partner = rows.find((r) => r.id === partnerId);
      if (partner === undefined || partner.parent !== row.id || !partner.orbit?.parent_relative) {
        fail(`body "${row.id}".orbit.barycentre_with`, `names "${String(partnerId)}", which is not a row orbiting "${row.id}" on parent-relative elements`);
      }
    }
    // A 4_gas_atmosphere row has an atmosphere by definition; "no_atmosphere"
    // there would be a silent wrong answer rather than the declared gap
    // Phase 4 is meant to find.
    if (row.class === '4_gas_atmosphere' && row.atmosphere.kind === 'no_atmosphere') {
      fail(`body "${row.id}".atmosphere.kind`, 'is "no_atmosphere" on a 4_gas_atmosphere row; use "no_parameters"');
    }
  }
  return rows;
}

/**
 * Attach the piecewise osculating sidecar (public/art/atlas/ephem-sets/,
 * written by tools/atlas/sample_elements.mjs) to the rows that name one in
 * their own `orbit.osculating`. Pure and throws on a malformed sidecar, same
 * as `validateBodies`; rows that carry no `osculating` metadata pass through
 * unchanged even if the sidecar happens to have an entry for their id.
 */
// Used only to sanity-check a sidecar's declared gm_km3_s2 against the
// mass_kg of the body it claims to orbit - G itself is known to about 1e-4
// relative, far worse than any body's own measured GM, so
// GM_MISMATCH_TOLERANCE below is loose on purpose: it catches a set attached
// to the wrong centre entirely (order-of-magnitude wrong), not a refinement
// of either constant.
const GM_MISMATCH_TOLERANCE = 0.01;

function expectedGmKm3S2(massKg: number): number {
  return (GRAVITATIONAL_CONSTANT_M3_KG_S2 * massKg) / 1e9;
}

/** One row's sets against its own declared metadata and its parent's GM. */
function checkOsculating(row: BodyRow, rowSets: OrbitSets, rows: readonly BodyRow[]): void {
  const decl = row.orbit!.osculating!;
  if (decl.count !== rowSets.epoch_jd.length) {
    fail(
      `body "${row.id}".orbit.osculating.count`,
      `declares ${decl.count} epochs but the sidecar set has ${rowSets.epoch_jd.length}`,
    );
  }
  const actualStart = rowSets.epoch_jd[0]!;
  const actualEnd = rowSets.epoch_jd[rowSets.epoch_jd.length - 1]!;
  if (Math.abs(decl.epoch_start_jd - actualStart) > 1e-6) {
    fail(
      `body "${row.id}".orbit.osculating.epoch_start_jd`,
      `declares ${decl.epoch_start_jd} but the sidecar set starts at ${actualStart}`,
    );
  }
  if (Math.abs(decl.epoch_end_jd - actualEnd) > 1e-6) {
    fail(
      `body "${row.id}".orbit.osculating.epoch_end_jd`,
      `declares ${decl.epoch_end_jd} but the sidecar set ends at ${actualEnd}`,
    );
  }
  // Every osculating row names a parent to check its GM against - "sol" for
  // the eight heliocentric rows today, an eventual satellite's actual
  // primary tomorrow - not only the parent-relative ones; a set attached to
  // the wrong centre errs in mean motion by roughly the square root of the
  // GM ratio, not a small drift.
  const parentId = row.parent;
  const parent = parentId !== null ? rows.find((r) => r.id === parentId) : undefined;
  if (parent === undefined) {
    fail(
      `body "${row.id}".orbit.osculating`,
      `names no parent in the registry to check its GM against (parent "${parentId}")`,
    );
  }
  const expected = expectedGmKm3S2(parent.mass_kg);
  const relError = Math.abs(rowSets.gm_km3_s2 - expected) / expected;
  if (relError > GM_MISMATCH_TOLERANCE) {
    fail(
      `body "${row.id}".orbit.osculating`,
      `carries gm_km3_s2=${rowSets.gm_km3_s2}, which is not "${parentId}"'s GM (expected ~${expected.toExponential(6)} km3/s2 from its mass_kg); check which centre's GM the sidecar sampled`,
    );
  }
}

export interface AttachOsculatingOptions {
  /**
   * Let a row that declares `orbit.osculating` come back with no `sets`, for a
   * document holding only some of the sampled rows. `loadBodies` passes this
   * because the deferred rows arrive later; a node tool reading the whole
   * directory at once leaves it off and keeps the "declared but absent" fault.
   */
  allowMissing?: boolean;
}

export function attachOsculating(
  rows: readonly BodyRow[],
  sidecarJson: unknown,
  options: AttachOsculatingOptions = {},
): readonly BodyRow[] {
  const top = object(sidecarJson, 'ephem-sets');
  const bodiesObj = object(top['bodies'], 'ephem-sets.bodies');
  const sets = new Map<string, OrbitSets>();
  for (const id of Object.keys(bodiesObj)) {
    const where = `ephem-sets.bodies.${id}`;
    const entry = object(bodiesObj[id], where);
    sets.set(id, readOrbitSets(entry['sets'], `${where}.sets`));
  }
  return rows.map((row) => {
    if (row.orbit === null || row.orbit.osculating === undefined) return row;
    const rowSets = sets.get(row.id);
    if (rowSets === undefined) {
      if (options.allowMissing === true) return row;
      fail(`body "${row.id}".orbit.osculating`, `names a sidecar set but the sidecar has none for "${row.id}"`);
    }
    checkOsculating(row, rowSets, rows);
    return { ...row, orbit: { ...row.orbit, sets: rowSets } };
  });
}

/** '.../bodies.json' -> '.../ephem-sets/', same directory as the registry. */
function sidecarDirFor(bodiesUrl: string): string {
  const slash = bodiesUrl.lastIndexOf('/');
  const dir = slash >= 0 ? bodiesUrl.slice(0, slash + 1) : '';
  return `${dir}ephem-sets/`;
}

/**
 * Which rows `loadBodies` fetches sets for, and which wait to be asked.
 *
 * A view names a system. Boot names the whole solar system - the orrery draws
 * every heliocentric ring - so a row about the frame origin is fetched with
 * the registry; without its sets ceres through saturn stand 1.5 to 5400
 * arcmin off, against ephem-check's own 10 arcmin gate. A parent-relative row
 * belongs to its parent's system and only a view that names that system reads
 * it: phobos and deimos are 93 percent of the sidecar's bytes and stand 0.022
 * and 0.011 arcmin off seen from the Sun, so nothing outside the Mars system
 * can draw the difference.
 */
function isDeferredRow(row: BodyRow): boolean {
  return row.orbit !== null && row.orbit.osculating !== undefined && row.orbit.parent_relative;
}

/**
 * `?eagerephem=1`: every sampled row's sets fetched with the registry, the way
 * the single-file sidecar did. `ephem-load-check.mjs --inject eager` reaches
 * it; nothing a player presses does.
 */
const EAGER_EPHEM = typeof location !== 'undefined' && TEST_SWITCHES
  && new URLSearchParams(location.search).get('eagerephem') === '1';
/**
 * `?noephemdemand=1`: `ensureOsculating` resolves without fetching anything,
 * so a deferred row stays on its secular fit for the whole session.
 * `ephem-load-check.mjs --inject no-demand` reaches it; nothing a player
 * presses does.
 */
const NO_EPHEM_DEMAND = typeof location !== 'undefined' && TEST_SWITCHES
  && new URLSearchParams(location.search).get('noephemdemand') === '1';
/**
 * `?ephemdemandlag=<ms>`: hold a demanded row's file back that long, so its
 * sets land under views that have already drawn from the secular fit.
 *
 * That late arrival is the condition the orrery's ring refresh answers, and
 * `?noephemdemand=1` cannot reach it: with the sets never arriving, a ring
 * and its body both stay on the secular fit and agree.
 */
const EPHEM_DEMAND_LAG_MS = (() => {
  if (typeof location === 'undefined') return 0;
  if (!TEST_SWITCHES) return 0;
  const raw = Number(new URLSearchParams(location.search).get('ephemdemandlag'));
  return Number.isFinite(raw) && raw > 0 ? raw : 0;
})();

// A demanded row's dropped request is tried 4 times in all, 1 s, 2 s and 4 s
// apart, before the row stays on its secular fit.
const SETS_ATTEMPTS = 4;
const SETS_RETRY_MS = 1000;
/**
 * How long one try waits for an answer before it counts as failed. A request
 * that is accepted but never answered (no error, no response) would
 * otherwise leave the fetch in flight for the whole session, and the row
 * bare with it; this makes a silent try no different from a dropped one for
 * the retry above.
 */
const SETS_TRY_TIMEOUT_MS = 8000;

/**
 * One body's sidecar file, or null when it is not there to be had. Only a
 * request that dropped (a network error or a 5xx) is tried again, and a try
 * that neither errors nor answers within `SETS_TRY_TIMEOUT_MS` counts as
 * dropped too.
 *
 * `attempts` is read after every failed try rather than once, because a caller
 * joining the fetch in flight can raise it. `onFailedTry` hears each try that
 * did not bring the file back, with `last` true on the one after which it
 * stops.
 */
async function fetchSets(
  dirUrl: string,
  id: string,
  attempts: () => number = () => 1,
  onFailedTry: (last: boolean) => void = () => {},
): Promise<unknown | null> {
  const url = `${dirUrl}${id}.json`;
  let failure = '';
  for (let tried = 1; ; tried++) {
    let res: Response | null = null;
    const bound = new AbortController();
    const boundTimer = setTimeout(() => bound.abort(), SETS_TRY_TIMEOUT_MS);
    try {
      res = await fetch(url, { cache: 'no-cache', signal: bound.signal });
    } catch (err) {
      if (bound.signal.aborted) {
        console.error(`bodies.json: "${id}"'s sets try ${tried} did not answer within `
          + `${SETS_TRY_TIMEOUT_MS} ms; counting it as failed`);
        failure = `no answer for ${url} within ${SETS_TRY_TIMEOUT_MS} ms`;
      } else {
        const reason = err instanceof Error ? err.message : String(err);
        failure = `network error fetching ${url}: ${reason}`;
      }
    } finally {
      clearTimeout(boundTimer);
    }
    if (res?.ok) return res.json();
    if (res !== null) failure = `GET ${url} returned ${res.status} ${res.statusText}`;
    const again = (res === null || res.status >= 500) && tried < attempts();
    onFailedTry(!again);
    if (!again) break;
    await new Promise<void>((resolve) => { setTimeout(resolve, SETS_RETRY_MS * 2 ** (tried - 1)); });
  }
  // A row that never got the sets it asked for - whether the request dropped
  // or came back missing - leaves the same seeded-off-its-stand hole a
  // rejected validation does, so it reports at the same (error) level rather
  // than warn.
  console.error(`bodies.json: osculating sets unavailable (${failure}); falling back to "${id}"'s secular fit`);
  return null;
}

/** The per-body files for `ids`, as one document `attachOsculating` takes. */
async function fetchSidecar(dirUrl: string, ids: readonly string[]): Promise<{ bodies: Record<string, unknown> }> {
  const docs = await Promise.all(ids.map(async (id) => [id, await fetchSets(dirUrl, id)] as const));
  const bodies: Record<string, unknown> = {};
  for (const [id, doc] of docs) {
    if (doc === null) continue;
    bodies[id] = doc;
  }
  return { bodies };
}

/** The sidecar directory each loaded registry came from, for a later demand. */
const SIDECAR_DIR = new WeakMap<readonly BodyRow[], string>();
/** A row's sets fetch, in flight or already landed. */
interface SetsFetch {
  /** Settles once the sets are attached or every try is spent. */
  readonly done: Promise<void>;
  /** Settles once `n` of its tries have failed. */
  failures(n: number): Promise<void>;
  /** Make at least `n` tries in all; no effect once it has stopped trying. */
  raise(n: number): void;
}

/** Per row id, the fetch already in flight or already landed. */
const SETS_IN_FLIGHT = new WeakMap<readonly BodyRow[], Map<string, SetsFetch>>();

/** Start `row`'s sets fetch with `attempts` tries and hold it in `inFlight`. */
function startSetsFetch(
  inFlight: Map<string, SetsFetch>,
  dirUrl: string,
  row: BodyRow,
  rows: readonly BodyRow[],
  attempts: number,
): SetsFetch {
  const id = row.id;
  let wanted = attempts;
  let failed = 0;
  const waiting: Array<{ n: number; resolve: () => void }> = [];
  // Only this fetch's own entry: a caller arriving after it stopped may already
  // hold a fresh one under the same id.
  const forget = (): void => {
    if (inFlight.get(id) === entry) inFlight.delete(id);
  };
  const onFailedTry = (last: boolean): void => {
    failed++;
    // Forgotten in the same turn as the last try's answer, so a caller arriving
    // after it starts a fresh fetch with its own tries instead of raising the
    // count on one that has already stopped. Even with its tries spent the row
    // must not be pinned to its secular fit for the whole session: the next
    // view naming this system asks again.
    if (last) forget();
    for (const w of waiting) if (w.n <= failed) w.resolve();
  };
  const attach = async (): Promise<void> => {
    if (EPHEM_DEMAND_LAG_MS > 0) {
      await new Promise<void>((resolve) => { setTimeout(resolve, EPHEM_DEMAND_LAG_MS); });
    }
    const doc = await fetchSets(dirUrl, id, () => wanted, onFailedTry);
    if (doc === null) return;
    const file = object(doc, `ephem-sets/${id}.json`);
    const rowSets = readOrbitSets(file['sets'], `ephem-sets/${id}.json.sets`);
    checkOsculating(row, rowSets, rows);
    (row.orbit as { sets?: OrbitSets }).sets = rowSets;
  };
  let done: Promise<void> = Promise.resolve();
  const entry: SetsFetch = {
    get done() { return done; },
    failures: (n) => (failed >= n
      ? Promise.resolve()
      : new Promise<void>((resolve) => { waiting.push({ n, resolve }); })),
    raise: (n) => { wanted = Math.max(wanted, n); },
  };
  // Held before the first try starts, so a try that fails at once still
  // forgets the entry it belongs to.
  inFlight.set(id, entry);
  done = attach();
  // A fault is worth raising to whoever asked, but the held promise must not
  // read as an unhandled rejection, and a row that failed has to be askable
  // again.
  done.catch(forget);
  return entry;
}

/**
 * Attach a deferred row's sets to the rows a view is already holding.
 *
 * Written into the row's own `orbit` rather than returned as new rows on
 * purpose: the sim, the Atlas and the orrery all hold the array `loadBodies`
 * gave them, and a set that arrived as a copy would be invisible to every one
 * of them. Idempotent and deduplicated per registry, so a per-frame caller
 * costs a map lookup.
 *
 * Ids with no deferred sets, and a fetch that fails, are no-ops: the row keeps
 * the secular fit it already answers on.
 *
 * `attempts` overrides the standard backoff for this call only. A caller
 * asking more tries than a fetch already in flight raises its count, and every
 * caller waits for its own count of that fetch's answers and no more. A spent
 * fetch forgets the row, so a low count does not stop a later caller from
 * getting the full retry.
 */
export async function ensureOsculating(
  rows: readonly BodyRow[],
  ids: readonly string[],
  attempts = SETS_ATTEMPTS,
): Promise<void> {
  if (NO_EPHEM_DEMAND) return;
  const dirUrl = SIDECAR_DIR.get(rows);
  if (dirUrl === undefined) return;
  let inFlight = SETS_IN_FLIGHT.get(rows);
  if (inFlight === undefined) {
    inFlight = new Map();
    SETS_IN_FLIGHT.set(rows, inFlight);
  }
  const waits: Promise<void>[] = [];
  for (const id of ids) {
    const held = inFlight.get(id);
    if (held !== undefined) {
      // A caller asking more tries than the fetch already under way gets them
      // from that fetch. One asking fewer still waits for that many of its
      // answers, however slow, but not for backoff it never asked for: past
      // its last failed try it moves on and leaves the fetch (and whoever else
      // is waiting on it) running.
      held.raise(attempts);
      waits.push(Promise.race([held.done, held.failures(attempts)]));
      continue;
    }
    const row = rows.find((r) => r.id === id);
    if (row === undefined || !isDeferredRow(row) || row.orbit?.sets !== undefined) continue;
    const started = startSetsFetch(inFlight, dirUrl, row, rows, attempts);
    // Its own count only, for the same reason: a later caller may raise it.
    waits.push(Promise.race([started.done, started.failures(attempts)]));
  }
  await Promise.all(waits);
}

/**
 * One registry per url, shared by everything that asks for it.
 *
 * The sim and the Atlas both call `loadBodies()` at boot, which used to be two
 * registry fetches and two sidecar fetches. It also has to be one array now
 * that sets arrive late: `ensureOsculating` writes into the rows its caller is
 * holding, and two separately fetched registries would each see only their own
 * demands answered.
 */
const REGISTRY_BY_URL = new Map<string, Promise<readonly BodyRow[]>>();

/**
 * Fetch and validate the registry, then attach the osculating sets the first
 * view needs. Only a fetch failure (network error, non-2xx) is treated as "no
 * sidecar today" - reports once and falls back to the affected rows' secular
 * fit. A sidecar that did fetch but fails `attachOsculating`'s validation is a
 * data bug, not an absence, and rethrows so the Atlas load fails loudly
 * instead of silently running on the wrong (or a corrupt) sidecar.
 *
 * The parent-relative rows are not fetched here; `ensureOsculating` pulls one
 * in when a view names its system.
 */
export async function loadBodies(
  url: string = '/art/atlas/bodies.json',
): Promise<readonly BodyRow[]> {
  const held = REGISTRY_BY_URL.get(url);
  if (held !== undefined) return held;
  const pending = loadBodiesUncached(url);
  REGISTRY_BY_URL.set(url, pending);
  // A failed load must not be the answer every later caller gets.
  pending.catch(() => { REGISTRY_BY_URL.delete(url); });
  return pending;
}

async function loadBodiesUncached(url: string): Promise<readonly BodyRow[]> {
  const res = await fetch(url, { cache: 'no-cache' });
  if (!res.ok) {
    throw new Error(`bodies.json: GET ${url} returned ${res.status} ${res.statusText}`);
  }
  const json: unknown = await res.json();
  const rows = validateBodies(json);
  const dirUrl = sidecarDirFor(url);

  const sampled = rows.filter((r) => r.orbit !== null && r.orbit.osculating !== undefined);
  const wanted = EAGER_EPHEM ? sampled : sampled.filter((r) => !isDeferredRow(r));
  const sidecar = await fetchSidecar(dirUrl, wanted.map((r) => r.id));
  const attached = attachOsculating(rows, sidecar, { allowMissing: true });
  SIDECAR_DIR.set(attached, dirUrl);
  return attached;
}

export function bodyById(rows: readonly BodyRow[], id: string): BodyRow | undefined {
  return rows.find((row) => row.id === id);
}

/**
 * Whether the globe drawn for this row is its own SHAPE rather than its datum
 * sphere: the height bake FLOORS at level 0, so the L0 field is the whole of
 * the relief the body carries and a radius read off it is the drawn ground
 * everywhere. Phobos, Deimos, Pallas, Hygiea, Psyche and Bennu today.
 *
 * Not "the height role reaches level 0", which Mars, Luna, Earth, Mercury,
 * Venus, Ceres, Vesta, Pluto and Charon do as well: their height floors far
 * below 0, so their L0 field is a coarse top of a pyramid the leaves draw
 * finer, and contact read off it would disagree with the ground on screen.
 * Those keep the mesh the globe is drawn as, in every reader.
 *
 * The one predicate `src/main.ts` and `src/atlas.ts` both read, so the sweep,
 * the pilot panel and the cruise-side surface reader cannot disagree about
 * which bodies carry a shape.
 */
export function isShapeBodyRow(row: BodyRow | null | undefined): boolean {
  return row?.bake?.height_floor_level === 0;
}

/**
 * Altitude above `radius_m` below which this body's OPAQUE deck hides the
 * ground, or null where the row declares none.
 *
 * `atmosphere.opaque_deck_m` alone, and only when positive: `corridor.deck_m`
 * is a giant's cloud-top REFERENCE altitude, not an opaque deck, so the
 * tile-stream clamp this feeds must not cover the four giants. A row's own
 * corridor altitudes are read straight off `row.corridor` where that concept
 * is wanted (`corridorGeometry`).
 */
export function deckAltitudeM(row: BodyRow): number | null {
  const atmo = row.atmosphere;
  if (atmo.kind === 'data' && atmo.opaque_deck_m !== undefined && atmo.opaque_deck_m > 0) {
    return atmo.opaque_deck_m;
  }
  return null;
}

/**
 * The deck's own cloud-top reflectance, falling back to `ground_albedo` the
 * same way `createAtmosphere` does - a row can carry an opaque deck with no
 * published `deck_albedo`, and the globe's uniform must not read a stale one.
 */
export function deckAlbedo(air: Atmosphere): readonly [number, number, number] {
  return air.deck_albedo ?? [air.ground_albedo, air.ground_albedo, air.ground_albedo];
}

/**
 * The ground's reflectance as a triple, for the constant-albedo consumers that
 * bounce sunlight off it back into the air.
 *
 * `ground_albedo` is the row's broadband number and stays the MEAN of what
 * comes back, so nothing about how bright the ground is moves; only the hue is
 * added, off the same `albedo_ramp` the plot mesh paints the terrain with. A
 * row that publishes no ramp keeps the grey it had.
 */
export function groundAlbedoRgb(air: Atmosphere): readonly [number, number, number] {
  const grey: readonly [number, number, number] =
    [air.ground_albedo, air.ground_albedo, air.ground_albedo];
  const lo = air.albedo_ramp_low;
  const hi = air.albedo_ramp_high;
  if (!lo || !hi) return grey;
  const mid: [number, number, number] = [(lo[0] + hi[0]) / 2, (lo[1] + hi[1]) / 2,
    (lo[2] + hi[2]) / 2];
  const mean = (mid[0] + mid[1] + mid[2]) / 3;
  if (!(mean > 0)) return grey;
  const k = air.ground_albedo / mean;
  return [mid[0] * k, mid[1] * k, mid[2] * k];
}

/** The vertical optical depth at 550 nm of the column above `aboveM` (the whole column by default): gas plus aerosol, a deck's own column included. */
export function columnTau550(air: Atmosphere, aboveM = 0): number {
  const z = Math.max(0, aboveM);
  return air.beta_r_per_m[1] * air.h_rayleigh_m * Math.exp(-z / air.h_rayleigh_m)
    + air.beta_m_550_per_m * air.h_aerosol_m * Math.exp(-z / air.h_aerosol_m);
}

/** The same column per channel (680/550/440 nm), the aerosol's `aerosol_ext_rgb` included. */
export function columnTauRgb(air: Atmosphere, aboveM = 0): [number, number, number] {
  const z = Math.max(0, aboveM);
  const gas = Math.exp(-z / air.h_rayleigh_m) * air.h_rayleigh_m;
  const haze = air.beta_m_550_per_m * air.h_aerosol_m * Math.exp(-z / air.h_aerosol_m);
  const ext = air.aerosol_ext_rgb ?? [1, 1, 1];
  return [0, 1, 2].map((c) => air.beta_r_per_m[c]! * gas + haze * ext[c]!) as [number, number, number];
}

/**
 * The share of the sun's direct beam the ground's light keeps, against `ref`'s.
 *
 * `ref` is the air the plot palette was drawn under (Mars's), so a column no
 * deeper than it keeps the whole beam - Earth's 0.11 and Mars's 0.50 both give
 * 1 - and only the excess depth takes it away: Titan's 8.8 leaves 2.5e-4 and
 * Venus's 49.7 nothing, where the ground is lit by the diffuse flux alone.
 */
export function directBeamShare(air: Atmosphere, ref: Atmosphere): number {
  return Math.exp(-Math.max(0, columnTau550(air) - columnTau550(ref)));
}

export type { BodiesFile };
